<template>
    <div class="content-wrapper">
        <div class="content">
            <template v-if="showDikidiTimezoneStep">
                <div class="timezone-stage">
                    <div class="timezone-card">
                        <span class="eyebrow">Часовой пояс</span>
                        <h2>Выберите часовой пояс проекта</h2>
                        <p>
                            Он будет использоваться для расписания уведомлений, отчётов и времени
                            обработки данных.
                        </p>
                        <div class="form-line">
                            <div class="field-label">Часовой пояс</div>
                            <TimeZonePicker
                                class="timezone-picker"
                                :value="selectedTimezone"
                                :recommendCurrentTimezone="true"
                                append-icon="mdi-chevron-down"
                                label=""
                                :persistent-label="false"
                                hide-details
                                @input="onTimezoneSelected"
                            />
                        </div>
                    </div>
                </div>
            </template>
            <template v-else>
                <template v-if="showChannelSelectionStep">
                    <ChannelSelectionStep @confirm="onChannelSelected" use-external-footer />
                </template>
                <template v-else-if="showMaxBusinessTokenStep">
                    <div class="titleBox mb-5">
                        Токен MAX Business
                    </div>
                    <div class="mb-3" style="font-size: 13px; color: #2A3B5199;">
                        Введите токен для подключения канала MAX Business
                    </div>
                    <v-text-field
                        v-model="maxBusinessToken"
                        outlined
                        dense
                        hide-details="auto"
                        placeholder="Введите токен"
                        :error-messages="maxBusinessTokenError"
                        @input="maxBusinessTokenError = ''"
                    />
                    <div class="mt-4 d-flex justify-space-between">
                        <v-btn
                            color="mainNew"
                            class="white--text mr-2 mt-4"
                            :elevation="0"
                            @click="onBackToChannelSelection"
                        >
                            <v-icon left>mdi-arrow-left</v-icon>
                            Назад
                        </v-btn>
                        <v-btn
                            color="mainNew"
                            class="white--text mt-4"
                            :elevation="0"
                            :disabled="!maxBusinessToken.trim()"
                            @click="onMaxBusinessTokenConfirmed"
                        >
                            Далее
                        </v-btn>
                    </div>
                </template>

                <template v-else>
                    <component
                        :is="connectionComponent"
                        :channelType="selectedChannelType"
                        :redirectOnConnect="false"
                        :showBack="false"
                        :embedded="true"
                        :providerTypeOverride="selectedProviderTypeOverride"
                        :max-business-token="maxBusinessToken"
                        @connector-created="onTempConnectorCreated"
                        @back="onConnectionBack"
                        @channel-connected="onChannelConnected"
                    />
                </template>
            </template>
        </div>
    </div>
</template>

<script setup>
import TimeZonePicker from '@/components/ui/TimeZonePicker.vue'
import useRouter from '@/hooks/useRouter'
import { computed, defineEmits, getCurrentInstance, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useProject } from '@root/src/hooks/useProject'
import api from '@/api/api'
import { CHANNEL_TYPES, CONNECTOR_TYPES, PROVIDER_TYPES } from '@/vars/general'
import ChannelSelectionStep from '@/components/project/onBoarding/ChannelSelectionStep.vue'
import ChannelConnectionView from '@/components/project/onBoarding/ChannelConnectionView.vue'
import WhatsappChannelConnectionViewOnboarding from '@/components/project/onBoarding/WhatsappChannelConnectionViewOnboarding.vue'
import MaxChannelConnectionViewOnboarding from '@/components/project/onBoarding/MaxChannelConnectionViewOnboarding.vue'
import TelegramChannelConnectionViewOnboarding from '@/components/project/onBoarding/TelegramChannelConnectionViewOnboarding.vue'
import { refreshProjectAndAssignChannelSlots } from '@/utils/onboardingHelpers'
const root = getCurrentInstance().proxy
const { project } = useProject()
const router = useRouter()
const emit = defineEmits(['refreshStep'])
const wizardNav = inject('onboardingWizardNav', null)
const setOnboardingBotViewKey = inject('setOnboardingBotViewKey', null)

const selectedTimezone = ref(null)
const timezoneConfirmed = ref(false)

const isDikidiProject = computed(() => {
    const connectors = project.value?.connectors || []
    return connectors.some((c) => c && c.connectorType === CONNECTOR_TYPES.dikidi)
})

const showDikidiTimezoneStep = computed(() => {
    if (!isDikidiProject.value) return false
    if (timezoneConfirmed.value) return false
    // Показываем шаг, если в самом проекте таймзона еще не задана
    return !project.value?.timezone
})

const selectedChannelType = ref(null)
const selectedProviderTypeOverride = ref(null)
const tempCreatedChannelConnectorId = ref(null)
const tempCreatedChannelType = ref(null)
const maxBusinessToken = ref('')
const maxBusinessTokenError = ref('')
const maxBusinessTokenConfirmed = ref(false)

const showChannelSelectionStep = computed(() => !selectedChannelType.value)
const showMaxBusinessTokenStep = computed(
    () =>
        selectedChannelType.value === CHANNEL_TYPES.maxBusiness &&
        !maxBusinessTokenConfirmed.value,
)

const connectionComponent = computed(() => {
    if (selectedChannelType.value === CHANNEL_TYPES.whatsappChannel) return WhatsappChannelConnectionViewOnboarding
    if ([CHANNEL_TYPES.maxChannel, CHANNEL_TYPES.maxBusiness].includes(selectedChannelType.value)) {
        return MaxChannelConnectionViewOnboarding
    }
    if (selectedChannelType.value === CHANNEL_TYPES.telegramChannel) {
        return TelegramChannelConnectionViewOnboarding
    }
    return ChannelConnectionView
})

/** Те же дефолтные провайдеры, что и в YC marketplace */
const resolveDefaultProviderType = (channelType) => {
    if (channelType === CHANNEL_TYPES.whatsappChannel) return PROVIDER_TYPES.whatsappDh
    // Онбординг сажает MAX на наш dh-max (не green-api-max)
    if (channelType === CHANNEL_TYPES.maxChannel) return PROVIDER_TYPES.maxDh
    if (channelType === CHANNEL_TYPES.telegramChannel) return PROVIDER_TYPES.telegramDh
    if (channelType === CHANNEL_TYPES.maxBusiness) return PROVIDER_TYPES.maxBusiness
    return null
}

const onTempConnectorCreated = (connectorId) => {
    tempCreatedChannelConnectorId.value = connectorId
    tempCreatedChannelType.value = selectedChannelType.value
}

const cleanupDraftChannelConnectors = async ({ keepChannelType, keepProviderType }) => {
    const projectId = project.value?.id
    if (!projectId) return

    try {
        await root.$store.dispatch('project/fetchProject', projectId)
    } catch (e) {
        // ignore
    }

    const freshProject = root.$store.getters.projectById?.(projectId) || project.value
    const channelTypes = [
        CHANNEL_TYPES.whatsappChannel,
        CHANNEL_TYPES.telegramChannel,
        CHANNEL_TYPES.maxChannel,
        CHANNEL_TYPES.maxBusiness,
    ]
    const connectors = freshProject?.connectors || []
    const draftsToDelete = connectors.filter((c) => {
        if (!c) return false
        if (!channelTypes.includes(c.connectorType)) return false
        if (c.isDraft !== true) return false

        // Оставляем только выбранный тип; для каналов с выбором провайдера учитываем providerType
        if (keepChannelType && c.connectorType === keepChannelType) {
            if (
                keepChannelType !== CHANNEL_TYPES.whatsappChannel &&
                keepChannelType !== CHANNEL_TYPES.telegramChannel &&
                keepChannelType !== CHANNEL_TYPES.maxChannel
            ) {
                return false
            }
            if (!keepProviderType) return false
            return c.providerType !== keepProviderType
        }

        return true
    })

    if (draftsToDelete.length === 0) return

    for (const connector of draftsToDelete) {
        if (!connector?.connectorId) continue
        try {
            await root.$store.dispatch('project/deleteConnector', {
                connectorId: connector.connectorId,
                skipLoading: true,
            })
        } catch (e) {
            // ignore
        }
    }

    try {
        await root.$store.dispatch('project/fetchProject', projectId)
    } catch (e) {
        // ignore
    }
}

onMounted(async () => {
    // подтягиваем проект, чтобы timezone/connectors были актуальны
    try {
        if (project.value?.id) {
            await root.$store.dispatch('project/fetchProject', project.value.id)
        }
    } catch (e) {
        // ignore
    }
    const tz = project.value?.timezone || sessionStorage.getItem('projectTimeZone')
    if (tz) selectedTimezone.value = tz
})

const onTimezoneSelected = (tz) => {
    selectedTimezone.value = tz
}

const persistSelectedTimezone = async () => {
    const tz = selectedTimezone.value
    if (!tz) return false
    sessionStorage.setItem('projectTimeZone', String(tz))
    try {
        const projectId = String(project.value?.id || '')
        if (projectId) {
            await api.networkFixer.updateProjectTimezone({ projectId, timezone: String(tz) })
            try {
                root.$store.commit('changeTimezone', { timezone: String(tz), projectId })
            } catch (e) {
                // ignore
            }
            try {
                await root.$store.dispatch('project/fetchProject', projectId)
            } catch (e) {
                // ignore
            }
        }
    } catch (e) {
        // ignore
    }
    return true
}

const onTimezoneNext = async () => {
    if (!selectedTimezone.value) return
    await persistSelectedTimezone()
    timezoneConfirmed.value = true
}

const onChannelSelected = async (channelType) => {
    let nextProviderTypeOverride = null
    if (channelType === CHANNEL_TYPES.whatsappChannel) {
        // Онбординг всегда подключает наш WhatsApp (dh-whatsapp), не легаси-бот
        nextProviderTypeOverride = PROVIDER_TYPES.whatsappDh
    } else if (channelType === CHANNEL_TYPES.maxChannel) {
        // Онбординг всегда подключает наш MAX (dh-max), не green-api-max
        nextProviderTypeOverride = PROVIDER_TYPES.maxDh
    } else if (channelType === CHANNEL_TYPES.telegramChannel) {
        // Онбординг всегда подключает наш Telegram (dh-telegram)
        nextProviderTypeOverride = PROVIDER_TYPES.telegramDh
    } else if (channelType === CHANNEL_TYPES.maxBusiness) {
        nextProviderTypeOverride = PROVIDER_TYPES.maxBusiness
    }

    // При смене выбора удаляем все draft-каналы, не соответствующие текущему выбору
    await cleanupDraftChannelConnectors({
        keepChannelType: channelType,
        keepProviderType: nextProviderTypeOverride,
    })

    selectedChannelType.value = channelType
    selectedProviderTypeOverride.value = nextProviderTypeOverride
    maxBusinessToken.value = ''
    maxBusinessTokenError.value = ''
    maxBusinessTokenConfirmed.value = channelType !== CHANNEL_TYPES.maxBusiness
}

const onMaxBusinessTokenConfirmed = () => {
    const token = maxBusinessToken.value?.trim()
    if (!token) {
        maxBusinessTokenError.value = 'Введите токен MAX Business'
        return
    }
    maxBusinessToken.value = token
    maxBusinessTokenError.value = ''
    maxBusinessTokenConfirmed.value = true
}

const onBackToChannelSelection = async () => {
    // На "Назад" очищаем созданные draft-каналы, чтобы не оставались лишние
    await cleanupDraftChannelConnectors({ keepChannelType: null, keepProviderType: null })
    selectedChannelType.value = null
    selectedProviderTypeOverride.value = null
    tempCreatedChannelConnectorId.value = null
    tempCreatedChannelType.value = null
    maxBusinessToken.value = ''
    maxBusinessTokenError.value = ''
    maxBusinessTokenConfirmed.value = false
}

const onConnectionBack = () => {
    // Как в YC: назад сразу к выбору канала (провайдер выбирается автоматически)
    onBackToChannelSelection()
}

/** Футер визарда: timezone / канал / QR */
const syncConnectionWizardNav = () => {
    if (!wizardNav) return
    if (showDikidiTimezoneStep.value) {
        wizardNav.set({
            canBack: false,
            canNext: Boolean(selectedTimezone.value),
            showNext: true,
            nextLoading: false,
            nextLabel: 'Далее',
            onNext: onTimezoneNext,
            onBack: null,
        })
        return
    }
    if (showChannelSelectionStep.value) {
        // ChannelSelectionStep сам управляет футером через useExternalFooter
        return
    }
    if (showMaxBusinessTokenStep.value) {
        wizardNav.set({
            canBack: true,
            canNext: false,
            showNext: false,
            nextLoading: false,
            onBack: onBackToChannelSelection,
            onNext: null,
        })
        return
    }
    // Экран QR / авторизации канала
    wizardNav.set({
        canBack: true,
        canNext: false,
        showNext: false,
        nextLoading: false,
        onBack: onConnectionBack,
        onNext: null,
    })
}

watch(
    [showChannelSelectionStep, showMaxBusinessTokenStep, showDikidiTimezoneStep, selectedChannelType, selectedTimezone],
    async () => {
        await nextTick()
        syncConnectionWizardNav()
    },
)

watch(
    [showDikidiTimezoneStep, showChannelSelectionStep, showMaxBusinessTokenStep, selectedChannelType],
    () => {
        if (!setOnboardingBotViewKey) return
        if (showDikidiTimezoneStep.value) {
            setOnboardingBotViewKey('dikidi-tz')
            return
        }
        if (showChannelSelectionStep.value) {
            setOnboardingBotViewKey('channel-selection')
            return
        }
        if (showMaxBusinessTokenStep.value) {
            setOnboardingBotViewKey('max-token')
            return
        }
        setOnboardingBotViewKey(`connection:${selectedChannelType.value || ''}`)
    },
    { immediate: true },
)

onBeforeUnmount(() => {
    setOnboardingBotViewKey?.('')
    wizardNav?.reset?.()
})

const onChannelConnected = async () => {
    // Для проектов не yClients создаём mailings-каскад и кастомный каскад «Администраторы»
    // (методы идемпотентные)
    try {
        const { error: cascadeError } = await api.networkFixer.cascadeDefaultMailingsCreate(
            String(project.value.id),
        )
        if (cascadeError) {
            throw new Error(
                cascadeError?.message ||
                    'Не удалось создать mailings-каскад по умолчанию. Подождите 10-15 секунд и нажмите "Сохранить" еще раз.',
            )
        }

        const { error: administratorsCascadeError } = await api.networkFixer.cascadeDefaultAdministratorsCreate(
            String(project.value.id),
        )
        if (administratorsCascadeError) {
            throw new Error(
                administratorsCascadeError?.message ||
                    'Не удалось создать каскад «Администраторы». Подождите 10-15 секунд и нажмите "Сохранить" еще раз.',
            )
        }

        // Принудительно выключаем onboarding только после успешного создания каскадов.
        project.value.isOnBoardingActive = false
        await root.$store.dispatch('project/updateProjectSettings', {
            projectId: project.value.id,
            settings: project.value,
        })
    } catch (e) {
        root.$store.dispatch(
            'callNotify',
            e?.message ||
                'Не удалось создать mailings-каскад. Повторите попытку.',
        )
        return
    }
    tempCreatedChannelConnectorId.value = null
    tempCreatedChannelType.value = null

    const projectId = project.value.id
    await refreshProjectAndAssignChannelSlots({
        store: root.$store,
        projectId,
    })

    router.router.push(`/project/${projectId}?showCascade=true`)
}

const cancel = () => {
    const projectId = project.value.id
    router.router.push(`/project/` + projectId)
}

root.$on('cancel', cancel)
</script>
<style lang="sass" scoped>
@import '@/vars.sass'

$ob-input-border: #D9DEEA

.content-wrapper
    margin-top: 0
    width: 100%
    max-width: 100%
    min-width: 0
    box-sizing: border-box
    .content
        align-items: stretch
        justify-content: center
        display: flex
        flex-direction: column
        width: 100%
        max-width: 100%
        min-width: 0
        padding-inline: 0
        box-sizing: border-box

.timezone-stage
    width: 100%
    max-width: 100%
    min-width: 0
    min-height: 100%
    display: flex
    align-items: center
    justify-content: center
    box-sizing: border-box
    // место под box-shadow карточки (иначе режет .wizard { overflow: hidden })
    padding: 24px 16px 28px
    overflow: visible

.timezone-card
    width: 100%
    max-width: 620px
    min-width: 0
    border: 1px solid $border-new
    border-radius: 26px
    background: #fff
    padding: 32px
    box-shadow: 0 16px 42px rgba(26, 26, 46, 0.08)
    box-sizing: border-box

    h2
        font-size: 32px
        line-height: 1.05
        letter-spacing: -0.055em
        margin: 0 0 12px
        font-weight: 800
        color: $dark-new
        overflow-wrap: anywhere

    p
        margin: 0 0 24px
        color: $muted-new
        font-size: 14px
        line-height: 1.45

.eyebrow
    display: inline-flex
    align-items: center
    gap: 6px
    margin-bottom: 10px
    color: $main-new
    font-size: 11px
    font-weight: 900
    text-transform: uppercase
    letter-spacing: 0.14em

.form-line
    display: grid
    gap: 0
    width: 100%
    max-width: 340px
    min-width: 0

.field-label
    font-size: 12px
    font-weight: 800
    color: #394155
    margin-bottom: 7px

.timezone-picker
    width: 100%
    max-width: 100%
    min-width: 0

    ::v-deep .base-input-outlined-wrapper
        width: 100%
        max-width: 100%
        min-width: 0
        box-sizing: border-box

    ::v-deep .v-input
        margin-top: 0
        padding-top: 0
        font-size: 14px
        width: 100%

    ::v-deep .v-input__control
        min-height: 44px
        width: 100%

    ::v-deep .v-input__slot
        margin-bottom: 0 !important
        min-height: 44px !important
        padding: 0 4px 0 13px !important
        background: #fff !important

    ::v-deep fieldset
        border: 1px solid $ob-input-border !important
        border-radius: 11px !important
        legend
            display: none

    ::v-deep .v-text-field--outlined.v-input--is-focused fieldset
        border-color: $main-new !important
        box-shadow: 0 0 0 4px rgba(82, 90, 213, 0.10)

    ::v-deep input
        color: $dark-new !important
        max-height: 44px
        padding: 0 !important
        caret-color: $dark-new

    ::v-deep .v-input__append-inner
        margin-top: 0 !important
        align-self: center
        padding: 0 8px 0 0 !important

    ::v-deep .v-icon
        color: $dark-new
        font-size: 22px

    ::v-deep .v-text-field__details
        display: none

@media (max-width: 700px)
    .timezone-stage
        padding: 16px 8px 20px
    .timezone-card
        padding: 20px
        border-radius: 20px

        h2
            font-size: 24px

    .form-line
        max-width: 100%
</style>
